import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, Send, PenLine, Sparkle } from "lucide-react";
import { useFamily } from "../context/FamilyContext";
import { api, errMsg, firstName } from "../lib/api";
import { useAutosave } from "../lib/useAutosave";
import { PageTitle, Chips, Loading, AiBadge, OriginalWords } from "../components/Bits";
import { VoiceInput } from "../components/VoiceInput";
import { ReportCard } from "../components/ReportCard";
import { RelationshipCheck } from "../components/RelationshipCheck";

const GROUPS = [
  ["mood", "Mood", ["Happy", "Calm", "Restless", "Sad", "Tired"]],
  ["meals", "Meals", ["Full meal", "Half a sandwich", "Snack", "Didn't eat much"]],
  ["activities", "Activities", ["TV", "Jeopardy", "Walk", "Cards", "Music", "Talking", "Photos"]],
  ["sleep", "Sleep", ["No nap", "Short nap", "Long nap"]],
  ["unusual", "Anything unusual?", ["Nothing", "Yes, see my note"]],
];

const AgreementNudge = ({ base }) => (
  <Link to={`${base}/agreement`} data-testid="report-agreement-nudge" className="flex items-start gap-3 rounded-2xl bg-hf-sun/30 p-5 text-lg">
    <Sparkle className="mt-1 h-5 w-5 shrink-0 text-hf-coraldark" />
    <span>It sounds like something could be smoother. Want to update your Expectations Agreement together? It only takes a few minutes.</span>
  </Link>
);

export function ReportDraft() {
  const { vid } = useParams();
  const { fid, base, isDemo, refresh, data } = useFamily();
  const [inp, setInp, clearInp] = useAutosave(`handoff_report_${vid}`, { chips: {}, words: "" });
  const [r, setR] = useState(null);
  const [busy, setBusy] = useState(false);
  const [edit, setEdit] = useState(false);
  const [sent, setSent] = useState(false);
  const [nudge, setNudge] = useState(false);

  const write = async () => {
    setBusy(true);
    try {
      const chips = Object.fromEntries(Object.entries(inp.chips).map(([k, v]) => [k, (v || []).join(", ")]));
      const res = await api.post(`/families/${fid}/visits/${vid}/report`, { chips, words: inp.words });
      setR(res.data);
    } catch (e) {
      toast.error(errMsg(e));
    }
    setBusy(false);
  };
  const saveEdit = async () => {
    const res = await api.put(`/families/${fid}/reports/${r.id}`, r.output);
    setR(res.data);
    setEdit(false);
  };
  const send = async () => {
    try {
      await api.post(`/families/${fid}/reports/${r.id}/send`);
      clearInp();
      setSent(true);
      refresh();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const cg = firstName(data.family.caregiver_name) || "the family";

  if (sent) return (
    <div className="space-y-6">
      <PageTitle eyebrow="Sent" title={`${cg} has your report. Thank you.`} />
      <ReportCard report={r} />
      <RelationshipCheck fid={fid} report={r} role="helper" onDone={(d) => setNudge(d.needs_agreement_update)} />
      {nudge && <AgreementNudge base={base} />}
      {isDemo && (
        <Link to={`${base}/reports/${r.id}`} data-testid="report-see-caregiver-view" className="hf-btn hf-btn-primary w-full">See it as {cg} does</Link>
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      <PageTitle eyebrow="Shift report" title="How did it go?" sub="Tap a few options, or record a 30 second voice note. Any language is fine." />
      {!r && (
        <>
          {GROUPS.map(([k, label, opts]) => (
            <div key={k} className="hf-card">
              <p className="mb-3 font-heading text-lg font-semibold">{label}</p>
              <Chips multi options={opts} value={inp.chips[k] || []} onChange={(v) => setInp({ ...inp, chips: { ...inp.chips, [k]: v } })} testid={`report-chip-${k}`} />
            </div>
          ))}
          <div className="hf-card">
            <p className="mb-3 font-heading text-lg font-semibold">In your own words</p>
            <VoiceInput testid="report-words" maxSeconds={30} value={inp.words} placeholder="Kumain siya ng half sandwich, nanood kami ng Jeopardy..." onChange={(v) => setInp((cur) => ({ ...cur, words: v }))} />
          </div>
          <button data-testid="report-write-button" disabled={busy} onClick={write} className="hf-btn hf-btn-primary w-full">
            <Sparkles className="h-5 w-5" /> {busy ? "Writing it up..." : "Write my report"}
          </button>
        </>
      )}
      {r && (
        <div className="space-y-4 hf-rise">
          <AiBadge>Check it, then send</AiBadge>
          {edit ? (
            <div className="hf-card space-y-3">
              <input data-testid="report-edit-headline" className="hf-input" value={r.output.headline} onChange={(e) => setR({ ...r, output: { ...r.output, headline: e.target.value } })} />
              <textarea data-testid="report-edit-story" className="hf-input min-h-[120px]" value={r.output.story} onChange={(e) => setR({ ...r, output: { ...r.output, story: e.target.value } })} />
              <button data-testid="report-edit-save" onClick={saveEdit} className="hf-btn hf-btn-primary w-full">Save</button>
            </div>
          ) : (
            <ReportCard report={r} testid="report-draft-card" />
          )}
          <OriginalWords label="Your original words" testid="report-original-words">
            {[Object.values(r.inputs.chips).filter(Boolean).join(" · "), r.inputs.words].filter(Boolean).join("\n")}
          </OriginalWords>
          <button data-testid="report-send-button" onClick={send} className="hf-btn hf-btn-coral w-full"><Send className="h-5 w-5" /> Approve and send to {cg}</button>
          <div className="grid grid-cols-2 gap-3">
            <button data-testid="report-edit-button" onClick={() => setEdit(true)} className="hf-btn hf-btn-ghost"><PenLine className="h-5 w-5" /> Edit</button>
            <button data-testid="report-rewrite-button" onClick={write} disabled={busy} className="hf-btn hf-btn-ghost">{busy ? "..." : "Rewrite"}</button>
          </div>
        </div>
      )}
    </div>
  );
}

function RestScore({ fid, report, onDone }) {
  const [score, setScore] = useState(report.rest_score);
  const pick = async (n) => {
    setScore(n);
    try {
      await api.post(`/families/${fid}/reports/${report.id}/rest`, { score: n });
      onDone();
      toast.success("Thank you. Noted.");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div data-testid="rest-score-card" className="hf-card">
      <p className="hf-eyebrow">For you</p>
      <h3 className="mt-1 font-heading text-xl font-semibold">How rested do you feel?</h3>
      <div className="mt-4 grid grid-cols-5 gap-2">
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n} data-testid={`rest-score-${n}`} onClick={() => pick(n)} className={`min-h-[60px] rounded-2xl text-2xl font-semibold transition-colors ${score === n ? "bg-hf-green text-hf-cream" : "bg-hf-cream text-hf-green ring-1 ring-hf-line"}`}>
            {n}
          </button>
        ))}
      </div>
      <div className="mt-2 flex justify-between text-sm text-hf-muted"><span>Running on empty</span><span>Well rested</span></div>
    </div>
  );
}

export function ReportView() {
  const { rid } = useParams();
  const { fid, base, roles, refresh } = useFamily();
  const [d, setD] = useState(null);
  const [nudge, setNudge] = useState(false);
  useEffect(() => {
    api.get(`/families/${fid}/reports/${rid}`).then((r) => {
      setD(r.data);
      setNudge(r.data.report.needs_agreement_update);
    }).catch(() => setD(false));
  }, [fid, rid]);
  if (d === null) return <Loading />;
  if (d === false) return <p className="text-lg">This report isn't available.</p>;
  const isCg = roles.includes("caregiver");
  return (
    <div className="space-y-6">
      <PageTitle eyebrow="Shift report" title={`A note from ${d.report.helper_name}`} back={base} />
      <div className="hf-rise"><ReportCard report={d.report} visit={d.visit} /></div>
      <OriginalWords label={`${d.report.helper_name}'s original words`} testid="report-view-original">
        {[Object.values(d.report.inputs?.chips || {}).filter(Boolean).join(" · "), d.report.inputs?.words].filter(Boolean).join("\n")}
      </OriginalWords>
      {d.visit?.started_at && (
        <p data-testid="report-visit-proof" className="text-base text-hf-muted">
          Verified visit: {new Date(d.visit.started_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })} to{" "}
          {new Date(d.visit.ended_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })} · {d.visit.start_loc?.approx}
        </p>
      )}
      {isCg && <RestScore fid={fid} report={d.report} onDone={refresh} />}
      {isCg && <RelationshipCheck fid={fid} report={d.report} role="caregiver" onDone={(x) => { setNudge(x.needs_agreement_update); refresh(); }} />}
      {nudge && <AgreementNudge base={base} />}
    </div>
  );
}
